import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useUserLevel, AdminLevel } from '../../context/admin';
import CreateSequence from './CreateSequence';
import AssignSequence from './AssignSequence';
import ViewPatterns from './ViewPatterns';
import PhaseBars, { PhaseLike } from './PhaseBars';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SequenceEntry {
  id: string;
  index: number;
  productCode: string;
  product: string;
  repeat: number;
  phase1: PhaseLike;
  phase2: PhaseLike;
  phase3: PhaseLike;
  phase4: PhaseLike;
  phase5: PhaseLike;
}

interface SequencesProps {
  users: Array<any>;
  uid: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PRODUCT_LABELS: Record<string, string> = {
  VM6MOAABLE: 'Back Recover 6 Motor',
  VM4MOAABLE: 'Back Recover 4 Motor',
  VM2MOBABLE: '2 Motor',
  VM1MOAABLE: '1 Motor',
};

const productLabel = (code: string) => PRODUCT_LABELS[code] ?? code;

const phasesOf = (entry: SequenceEntry): PhaseLike[] =>
  [entry.phase1, entry.phase2, entry.phase3, entry.phase4, entry.phase5];

const fetchAllSequences = async (): Promise<SequenceEntry[]> => {
  const snapshot = await getDocs(collection(db, 'sequences'));
  const entries: SequenceEntry[] = [];
  snapshot.forEach(d => {
    const data = d.data();
    Object.keys(data).forEach(key => {
      if (key.startsWith('sequence')) {
        entries.push({ id: key, productCode: d.id, ...data[key] } as SequenceEntry);
      }
    });
  });
  return entries;
};

// ─── Shared modal shell ────────────────────────────────────────────────────────

const ModalShell: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({
  title, onClose, children,
}) => (
  <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
    <div className="bg-white h-5/6 w-11/12 lg:w-3/4 xl:w-2/3 p-8 rounded-3xl shadow-2xl relative overflow-auto">
      <button
        className="btn btn-sm btn-circle absolute right-4 top-4"
        onClick={onClose}
        aria-label="Close"
      >✕</button>
      <h2 className="text-3xl font-semibold text-gray-800 mb-8">{title}</h2>
      {children}
    </div>
  </div>
);

// ─── Sequence card (grid tile) ─────────────────────────────────────────────────

const SequenceCard: React.FC<{ entry: SequenceEntry; onClick: () => void }> = ({ entry, onClick }) => (
  <button
    onClick={onClick}
    className="group text-left bg-white border border-gray-200 rounded-2xl p-5 hover:border-MyovoltPurple hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-MyovoltPurple/30"
  >
    <div className="flex items-start justify-between mb-3 gap-2">
      <div className="min-w-0">
        <p className="font-semibold text-gray-900 truncate leading-tight">{productLabel(entry.productCode)}</p>
        <p className="text-xs text-gray-400 mt-0.5">{entry.productCode} · #{entry.index}</p>
      </div>
      {entry.repeat > 0 && (
        <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full border font-medium bg-blue-50 text-blue-700 border-blue-200">
          Repeats ×{entry.repeat}
        </span>
      )}
    </div>
    <div className="flex justify-center py-3 bg-gray-50 rounded-xl">
      <PhaseBars phases={phasesOf(entry)} height={56} />
    </div>
  </button>
);

// ─── Sequence detail panel ─────────────────────────────────────────────────────

const SequenceDetailPanel: React.FC<{
  entry: SequenceEntry;
  onClose: () => void;
  onModify: () => void;
}> = ({ entry, onClose, onModify }) => {
  const phases = phasesOf(entry);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-xl max-h-[90vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden">

        <div className="px-7 pt-7 pb-5 border-b border-gray-100 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 leading-tight">{productLabel(entry.productCode)}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{entry.productCode} · Sequence #{entry.index}</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button className="btn btn-sm btn-outline" onClick={onModify}>Modify</button>
            <button className="btn btn-sm btn-circle btn-ghost" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-7 py-5 space-y-6">
          <div className="flex justify-center py-6 bg-gray-50 rounded-xl">
            <PhaseBars phases={phases} height={96} />
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Phases</p>
            <div className="divide-y divide-gray-100 rounded-2xl border border-gray-100 overflow-hidden">
              {phases.map((p, i) => {
                const active = p.intensity > 0 && p.length > 0;
                return (
                  <div key={i} className="flex items-center justify-between px-4 py-3 bg-white">
                    <span className="text-sm font-medium text-gray-700">Phase {i + 1}</span>
                    {active ? (
                      <span className="text-sm text-gray-500">
                        Pattern {p.pattern} · {p.intensity}% intensity · {p.length} min
                      </span>
                    ) : (
                      <span className="text-sm text-gray-300">Off</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {entry.repeat > 0 && (
            <p className="text-sm text-gray-500">
              Repeats <strong className="text-gray-700">{entry.repeat}</strong> time{entry.repeat > 1 ? 's' : ''}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Overview — browse, create, modify, assign ─────────────────────────────────

const SequenceOverview: React.FC<{ users: any[]; uid: string; onClose: () => void }> = ({ users, uid, onClose }) => {
  const [entries, setEntries] = useState<SequenceEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [viewing, setViewing] = useState<SequenceEntry | null>(null);
  const [editing, setEditing] = useState<SequenceEntry | null>(null);
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [viewingPatterns, setViewingPatterns] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        setEntries(await fetchAllSequences());
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return entries
      .filter(e => !productFilter || e.productCode === productFilter)
      .filter(e =>
        !q ||
        productLabel(e.productCode).toLowerCase().includes(q) ||
        e.productCode.toLowerCase().includes(q) ||
        String(e.index).includes(q)
      )
      .sort((a, b) => a.productCode.localeCompare(b.productCode) || a.index - b.index);
  }, [entries, search, productFilter]);

  return (
    <>
      <ModalShell title="Sequence Overview" onClose={onClose}>

        {/* ── Controls ── */}
        <div className="flex flex-wrap gap-3 mb-5">
          <div className="relative flex-1 min-w-[200px]">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search sequences…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="input input-bordered w-full pl-9"
            />
          </div>
          <select
            className="select select-bordered"
            value={productFilter}
            onChange={e => setProductFilter(e.target.value)}
          >
            <option value="">All Products</option>
            {Object.entries(PRODUCT_LABELS).map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
          <button className="btn btn-outline" onClick={() => setAssigning(true)}>Assign to Users</button>
          <button className="btn btn-outline" onClick={() => setViewingPatterns(true)}>View Patterns</button>
        </div>

        {/* ── Grid ── */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <span className="loading loading-spinner loading-lg text-MyovoltPurple" />
            <p className="text-sm text-gray-400">Loading sequences…</p>
          </div>
        ) : (
          <>
            {filtered.length === 0 && (search || productFilter) && (
              <p className="text-center text-gray-400 py-6">No sequences match your search.</p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map(entry => (
                <SequenceCard key={`${entry.productCode}-${entry.id}`} entry={entry} onClick={() => setViewing(entry)} />
              ))}
              <button
                onClick={() => setCreating(true)}
                className="flex flex-col items-center justify-center gap-2 min-h-[140px] border-2 border-dashed border-gray-300 rounded-2xl p-5 text-gray-400 hover:border-MyovoltPurple hover:text-MyovoltPurple transition-colors focus:outline-none focus:ring-2 focus:ring-MyovoltPurple/30"
              >
                <span className="text-3xl leading-none font-light">+</span>
                <span className="text-sm font-medium">New Sequence</span>
              </button>
            </div>
          </>
        )}
      </ModalShell>

      {viewing && (
        <SequenceDetailPanel
          entry={viewing}
          onClose={() => setViewing(null)}
          onModify={() => { setEditing(viewing); setViewing(null); }}
        />
      )}

      {editing && (
        <CreateSequence
          onClose={() => setEditing(null)}
          editingSequence={{ productCode: editing.productCode, sequence: editing }}
        />
      )}

      {creating && (
        <CreateSequence onClose={() => setCreating(false)} />
      )}

      {assigning && (
        <AssignSequence onClose={() => setAssigning(false)} uid={uid} users={users} />
      )}

      {viewingPatterns && (
        <ViewPatterns onClose={() => setViewingPatterns(false)} />
      )}
    </>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const Sequences: React.FC<SequencesProps> = ({ users, uid }) => {
  const { currentUserLevel } = useUserLevel();
  const [isOpen, setIsOpen] = useState(false);

  if (currentUserLevel !== AdminLevel.SUPER_ADMIN) return null;

  return (
    <>
      <button className="btn mx-2 btn-inactive" onClick={() => setIsOpen(true)}>Sequences</button>
      {isOpen && (
        <SequenceOverview users={users} uid={uid} onClose={() => setIsOpen(false)} />
      )}
    </>
  );
};

export default Sequences;
